<template>
  <super-admin-layout>
    <v-container fluid class="renewals-page">
      <v-row>
        <v-col cols="12" md="3">
          <v-card flat>
            <v-card-actions>
              <v-text-field placeholder="Search by company, plan or company id" prepend-icon="fa-search" v-model="search"
                type="text" clearable clear-icon="fa-times" @click:clear="resetSearch" @keyup="applySearch"
                class="search-box"></v-text-field>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card flat class="v-card-bg-none">
            <v-card-actions>
              <v-select :items="statusOptions" :clearable="true" placeholder="Status" item-value="id" item-text="name"
                v-on:change="filterByStatus($event)" class="search-box"></v-select>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col cols="12" md="6" class="d-flex align-center">
          <p class="renewals-intro mb-0">
            Three year agreements, soonest notice deadline first. An agreement renews for another 36 months unless
            notice is given at least 90 days before the end of its term. Record a notice here and Stripe stops
            charging when the agreement ends.
          </p>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-skeleton-loader v-if="loading && isTabletOrDesktop" type="table" :types="{ 'table-heading': '' }"></v-skeleton-loader>
          <v-skeleton-loader v-if="loading && !isTabletOrDesktop" type="table" :types="{ 'table-heading': '', 'table-thead' : 'heading@1' ,'table-row':'text@1'}" class="slider-table"></v-skeleton-loader>
          <v-data-table :headers="headers" :options.sync="options" :server-items-length="serverItemsLength"
            v-show="!loading" :items="renewals" item-key="id" :item-class="rowClass"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }">

            <template v-slot:item.company_name="{ item }">
              <router-link :to="'/super-admin/company/' + item.company_id" class="company-link">
                {{ item.company_name || ('Company ' + item.company_id) }}
              </router-link>
              <div class="grey--text caption">id {{ item.company_id }}</div>
            </template>

            <template v-slot:item.term_started_at="{ item }">{{ day(item.term_started_at) }}</template>
            <template v-slot:item.term_ends_at="{ item }">{{ day(item.term_ends_at) }}</template>

            <template v-slot:item.notice_deadline_at="{ item }">
              <span>{{ day(item.notice_deadline_at) }}</span>
              <div v-if="item.days_to_deadline !== null" :class="deadlineClass(item)" class="caption">
                {{ deadlineText(item) }}
              </div>
            </template>

            <template v-slot:item.renewal_status="{ item }">
              <v-chip small :color="statusColor(item.renewal_status)" text-color="white">
                {{ statusLabel(item.renewal_status) }}
              </v-chip>
              <div v-if="item.renewal_status == 'notice_received'" class="caption mt-1">
                Ends {{ day(item.agreement_ends_at) }}<br />
                <span v-if="item.stripe_notice_pending" class="orange--text text--darken-2">Stripe not yet told, retried daily</span>
                <span v-else class="green--text text--darken-2">Stripe stops charging then</span>
              </div>
              <div v-else-if="item.renewed_count > 0" class="caption mt-1">Renewed {{ item.renewed_count }} time{{ item.renewed_count == 1 ? '' : 's' }}</div>
            </template>

            <template v-slot:item.id="{ item }">
              <v-tooltip bottom v-if="item.renewal_status == 'auto_renew' || item.renewal_status == 'renewed'">
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" outlined small color="secondary" @click.prevent="showNoticeDialog(item)">
                    Notice received
                  </v-btn>
                </template>
                <span>Record a notice of non-renewal for this agreement</span>
              </v-tooltip>
              <v-tooltip bottom v-else-if="item.renewal_status == 'notice_received'">
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" outlined small color="secondary" @click.prevent="showWithdrawDialog(item)">
                    Withdraw notice
                  </v-btn>
                </template>
                <span>Recorded {{ day(item.notice_received_at) }} by {{ item.notice_recorded_by }} ({{ item.notice_given_by }}){{ item.notice_note ? ': ' + item.notice_note : '' }}</span>
              </v-tooltip>
              <span v-else class="grey--text caption">Ended {{ day(item.agreement_ends_at) }}</span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="noticeDialog" max-width="560px" scrollable persistent>
      <v-card>
        <v-card-title :class="this.$gf.headerColor()" style="color: white;">
          <span>Notice of non-renewal</span>
        </v-card-title>
        <v-container class="dialog-container">
          <v-row>
            <v-col cols="12">
              <p>
                <strong>{{ noticeItem.company_name }}</strong>: current term ends {{ day(noticeItem.term_ends_at) }},
                notice deadline {{ day(noticeItem.notice_deadline_at) }}.
              </p>
              <p :class="noticeIsLate ? 'error--text' : ''">
                <span v-if="!noticeIsLate">Notice by the deadline ends the agreement at the end of this term, {{ day(noticeItem.term_ends_at) }}.</span>
                <span v-else>This notice is after the deadline, so under clause 14.2 it takes effect at the end of the following term, not this one.</span>
              </p>
              <v-radio-group v-model="noticeGivenBy" row class="mt-0">
                <v-radio label="Received from the customer" value="customer"></v-radio>
                <v-radio label="Given by IMPROVE" value="improve"></v-radio>
              </v-radio-group>
              <v-text-field v-model="noticeReceivedAt" label="Date notice was received (YYYY-MM-DD)" placeholder="Today if left blank"
                :error-messages="messages.received_at" filled></v-text-field>
              <v-textarea v-model="noticeNote" label="Note (who, how it arrived, anything to remember)" rows="3"
                :error-messages="messages.note" filled></v-textarea>
              <p class="caption mb-0">
                Stripe is told to stop charging when the agreement ends. The account is not closed by this; that stays a
                manual step when the agreement ends, and you will be emailed on the day.
              </p>
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions class="btn-section pa-5">
          <v-spacer></v-spacer>
          <template v-if="noticeLoading">
            <v-btn color="primary" loading></v-btn>
          </template>
          <template v-else>
            <v-btn color="primary" @click="closeNoticeDialog">Cancel</v-btn>
            <v-btn color="primary" @click="processNotice">Record notice</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="withdrawDialog" max-width="520px" scrollable persistent>
      <v-card>
        <v-card-title :class="this.$gf.headerColor()" style="color: white;">
          <span>Withdraw notice</span>
        </v-card-title>
        <v-container class="dialog-container">
          <v-row>
            <v-col cols="12">
              <p>
                <strong>{{ withdrawItem.company_name }}</strong> has notice recorded ({{ withdrawItem.notice_given_by }},
                {{ day(withdrawItem.notice_received_at) }}). Withdrawing it puts the agreement back to renewing
                automatically and tells Stripe to carry on billing.
              </p>
              <v-textarea v-model="withdrawNote" label="Why (for the audit trail)" rows="2" :error-messages="messages.note" filled></v-textarea>
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions class="btn-section pa-5">
          <v-spacer></v-spacer>
          <template v-if="withdrawLoading">
            <v-btn color="primary" loading></v-btn>
          </template>
          <template v-else>
            <v-btn color="primary" @click="closeWithdrawDialog">Cancel</v-btn>
            <v-btn color="primary" @click="processWithdraw">Withdraw notice</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </super-admin-layout>
</template>

<script>
import SuperAdminLayout from '@/layouts/SuperAdmin';
import renewalAPI from '@/api/renewal.js';

export default {
  name: 'SuperAdminRenewals',
  components: {
    SuperAdminLayout,
  },
  data() {
    return {
      headers: [
        { text: 'Company', value: 'company_name', sortable: true },
        { text: 'Plan', value: 'plan_slug', sortable: true },
        { text: 'Term start', value: 'term_started_at', sortable: true },
        { text: 'Term end', value: 'term_ends_at', sortable: true },
        { text: 'Notice deadline', value: 'notice_deadline_at', sortable: true },
        { text: 'Status', value: 'renewal_status', sortable: true },
        { text: '', value: 'id', sortable: false, align: 'end' },
      ],
      renewals: [],
      search: '',
      searchParams: '',
      selectedStatus: '',
      statusOptions: [
        { id: 'auto_renew', name: 'Renewing automatically' },
        { id: 'renewed', name: 'Renewed for a further term' },
        { id: 'notice_received', name: 'Notice received' },
        { id: 'ended', name: 'Ended' },
      ],
      pagination: {},
      options: { page: 1, itemsPerPage: 25, sortBy: ['notice_deadline_at'], sortDesc: [false] },
      loading: false,
      timer: null,
      noticeDialog: false,
      noticeItem: {},
      noticeGivenBy: 'customer',
      noticeReceivedAt: '',
      noticeNote: '',
      noticeLoading: false,
      withdrawDialog: false,
      withdrawItem: {},
      withdrawNote: '',
      withdrawLoading: false,
      messages: {},
      errorMessage: 'Oops, something went wrong! Please check information is correct.',
    };
  },
  computed: {
    serverItemsLength: function () {
      if (this.pagination && this.pagination.total) {
        return this.pagination.total;
      }
      return this.renewals.length;
    },
    params: function () {
      const o = this.options;
      const limit = o.itemsPerPage === -1 ? 100 : o.itemsPerPage;
      let q = '?page=' + (o.page || 1) + '&limit=' + limit;
      if (o.sortBy && o.sortBy.length) {
        q += '&sort=' + encodeURIComponent(o.sortBy[0]) + '&desc=' + (o.sortDesc && o.sortDesc[0] ? '1' : '0');
      }
      return q;
    },
    isTabletOrDesktop() {
      return this.$vuetify.breakpoint.width >= 820;
    },
    noticeIsLate() {
      if (!this.noticeItem || !this.noticeItem.notice_deadline_at) return false;
      const received = this.noticeReceivedAt ? new Date(this.noticeReceivedAt) : new Date();
      return received.getTime() > new Date(this.noticeItem.notice_deadline_at.replace(' ', 'T')).getTime();
    },
  },
  watch: {
    options: {
      handler() {
        this.getRenewals();
      },
      deep: true,
    },
  },
  methods: {
    getRenewals() {
      this.loading = true;
      renewalAPI
        .getRenewals(this.params, this.searchParams, this.selectedStatus, '')
        .then((resp) => {
          this.renewals = resp.data.data;
          this.pagination = resp.data.meta.pagination;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.$snotify.error(this.errorMessage);
        });
    },
    applySearch() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.options.page = 1;
        this.searchParams = this.search ? '&search=' + encodeURIComponent(this.search) : '';
        this.getRenewals();
      }, 700);
    },
    resetSearch() {
      this.options.page = 1;
      this.search = '';
      this.applySearch();
    },
    filterByStatus(status) {
      this.selectedStatus = status ? '&status=' + status : '';
      this.options.page = 1;
      this.getRenewals();
    },
    day(value) {
      if (!value) return '';
      return String(value).slice(0, 10);
    },
    deadlineText(item) {
      const d = item.days_to_deadline;
      if (d === 0) return 'Deadline today';
      if (d < 0) return Math.abs(d) + ' days past, renews at term end';
      return d + ' days left';
    },
    deadlineClass(item) {
      const d = item.days_to_deadline;
      if (d === null) return '';
      if (d <= 30) return 'error--text';
      if (d <= 60) return 'orange--text text--darken-2';
      return 'grey--text';
    },
    statusColor(status) {
      return { auto_renew: 'success', renewed: 'success', notice_received: 'orange darken-2', ended: 'grey' }[status] || 'grey';
    },
    statusLabel(status) {
      return { auto_renew: 'Renewing', renewed: 'Renewed', notice_received: 'Notice received', ended: 'Ended' }[status] || status;
    },
    rowClass(item) {
      return item.renewal_status == 'ended' ? 'renewal-ended' : '';
    },
    showNoticeDialog(item) {
      this.noticeItem = item;
      this.noticeGivenBy = 'customer';
      this.noticeReceivedAt = '';
      this.noticeNote = '';
      this.messages = {};
      this.noticeDialog = true;
    },
    closeNoticeDialog() {
      this.noticeDialog = false;
      this.noticeItem = {};
      this.messages = {};
    },
    processNotice() {
      this.noticeLoading = true;
      this.messages = {};
      renewalAPI
        .recordNotice(this.noticeItem.id, {
          given_by: this.noticeGivenBy,
          received_at: this.noticeReceivedAt || null,
          note: this.noticeNote || null,
        })
        .then((resp) => {
          this.noticeLoading = false;
          this.closeNoticeDialog();
          const s = resp.data && resp.data.stripe;
          if (s && s.applied) {
            this.$snotify.success('Notice recorded. Stripe will stop charging when the agreement ends.');
          } else {
            this.$snotify.warning('Notice recorded. Stripe could not be updated yet; it is retried every day until it is.');
          }
          this.getRenewals();
        })
        .catch((err) => {
          this.noticeLoading = false;
          this.messages = this.$gf.errorResponse(err) || {};
          const msg = err && err.response && err.response.data && err.response.data.message;
          this.$snotify.error(msg || this.errorMessage);
        });
    },
    showWithdrawDialog(item) {
      this.withdrawItem = item;
      this.withdrawNote = '';
      this.messages = {};
      this.withdrawDialog = true;
    },
    closeWithdrawDialog() {
      this.withdrawDialog = false;
      this.withdrawItem = {};
      this.messages = {};
    },
    processWithdraw() {
      this.withdrawLoading = true;
      this.messages = {};
      renewalAPI
        .withdrawNotice(this.withdrawItem.id, this.withdrawNote || null)
        .then(() => {
          this.withdrawLoading = false;
          this.closeWithdrawDialog();
          this.$snotify.success('Notice withdrawn. The agreement renews automatically again.');
          this.getRenewals();
        })
        .catch((err) => {
          this.withdrawLoading = false;
          const msg = err && err.response && err.response.data && err.response.data.message;
          this.$snotify.error(msg || this.errorMessage);
        });
    },
  },
};
</script>
<style>
.renewals-page .renewal-ended {
  opacity: 0.55;
}
.renewals-page .renewals-intro {
  font-size: 13px;
  color: #555;
}
.renewals-page .company-link {
  text-decoration: none;
  font-weight: 600;
}
</style>
